// Bridges a Viewer 2.0 SideConfig to a concrete style JSON, reusing the 1.0
// viewer's API client (no backend change) and C2's tileset override. Lives in
// the react/ layer because it depends on the app's apiCall utilities; the
// engine stays renderer-only.

import { applyTilesetUrl } from "../engine/style";
import { StyleJson } from "../engine";
import { SideConfig } from "../state";
import {
    fetchResourceFromUrl,
    fetchStylesheet,
    fetchStylesheets,
    formatLiveBasemapData,
    getStylesheetUrl,
    getVersionsForDropdown,
} from "../../../utils/apiCall";

// Re-exported so the controller can build status-grouped version options
// (ACTIVE/EXPERIMENTAL/DEPRECATED) without importing 1.0 utils directly.
export { getVersionsForDropdown };

/**
 * Resolves a side config to a ready-to-render style JSON:
 *  - "select": look up the stylesheet URL from the catalog for the chosen
 *    environment, then fetch the versioned style.
 *  - "manual": fetch the pasted stylesheet URL directly.
 * Applies a tileset URL override (C2) when one is set. Returns null on failure.
 */
export async function fetchStylesheetJson(config: SideConfig): Promise<StyleJson | null> {
    try {
        let styleJson: StyleJson | null = null;

        if (config.selectionType === "manual") {
            if (!config.manualStylesheetUrl) return null;
            styleJson = await fetchResourceFromUrl(config.manualStylesheetUrl);
        } else {
            if (!config.environment || !config.stylesheetId || !config.stylesheetVersion) {
                return null;
            }
            const catalog = formatLiveBasemapData(await fetchStylesheets(config.environment));
            const url = getStylesheetUrl(config.stylesheetId, catalog.formatted);
            styleJson = await fetchStylesheet(url, config.stylesheetVersion, config.environment);
        }

        if (!styleJson) return null;

        const tilesetUrl = config.tilesetUrl.trim();
        if (tilesetUrl) {
            styleJson = await applyTilesetUrl(tilesetUrl, styleJson);
        }
        return styleJson;
    } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Viewer 2.0: failed to load stylesheet", err);
        return null;
    }
}

/** Fetches the catalog for an environment, formatted for the dropdowns. */
export async function fetchCatalog(environment: string) {
    return formatLiveBasemapData(await fetchStylesheets(environment));
}
